import JSZip from "jszip";
import imageCompression from "browser-image-compression";

// Target 400KB
const MAX_SIZE_MB = 0.4;

export async function compressKmzImageCompressor(
  file: File,
): Promise<FileList> {
  const zip = new JSZip();
  const kmzData = await zip.loadAsync(file);
  const filesToProcess: string[] = [];

  // 1. Kumpulkan file gambar
  kmzData.forEach((relativePath, fileEntry) => {
    if (!fileEntry.dir) {
      const ext = relativePath.split(".").pop()?.toLowerCase();
      if (["jpg", "jpeg", "png"].includes(ext || "")) {
        filesToProcess.push(relativePath);
      }
    }
  });

  // 2. Fungsi Kompresi menggunakan browser-image-compression
  const processPromises = filesToProcess.map(async (fullPath) => {
    const fileEntry = kmzData.file(fullPath);
    if (!fileEntry) return;

    // Ambil file sebagai Blob/File
    const fileBuffer = await fileEntry.async("arraybuffer");
    const ext = fullPath.split(".").pop()?.toLowerCase();
    const mime = ext === "png" ? "image/png" : "image/jpeg";

    // Kita bungkus jadi File object karena library ini butuh input File
    const originalFile = new File([fileBuffer], fullPath, { type: mime });

    // Cek dulu: Kalau sudah < 400KB, jangan diapa-apain (biar cepat & jernih)
    if (originalFile.size / 1024 / 1024 <= MAX_SIZE_MB) {
      console.info(`[SKIP] ${fullPath} is already < 400KB`);
      return;
    }

    try {
      // --- THE MAGIC IS HERE ---
      // Library ini akan otomatis loop menurunkan quality & size
      // sampai outputnya <= maxSizeMB
      const compressedBlob = await imageCompression(originalFile, {
        maxSizeMB: MAX_SIZE_MB, // Target 400KB
        maxWidthOrHeight: 1920, // Opsional: Batas resolusi awal
        useWebWorker: true, // Biar UI gak nge-freeze
        fileType: "image/jpeg", // Force output jadi JPEG biar size makin kecil
        initialQuality: 0.7, // Start quality
      });

      console.info(
        `[OK] ${fullPath}: ${(originalFile.size / 1024).toFixed(2)}KB -> ${(
          compressedBlob.size / 1024
        ).toFixed(2)}KB`,
      );

      // Update ZIP
      // Karena kita force output jadi JPEG, kita harus handle ganti ekstensi jika aslinya PNG
      if (ext === "png") {
        const newPath = fullPath.replace(/\.png$/i, ".jpg");
        kmzData.remove(fullPath); // Hapus file .png lama
        kmzData.file(newPath, compressedBlob); // Masukkan file .jpg baru
      } else {
        kmzData.remove(fullPath);
        kmzData.file(fullPath, compressedBlob);
      }
    } catch (error) {
      console.error(`Gagal kompres ${fullPath}`, error);
      // Jika gagal, biarkan file asli (tidak di-remove/replace)
    }
  });

  await Promise.all(processPromises);

  // 3. Generate KMZ Final
  const compressedKmzBlob: Blob = await kmzData.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });

  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(
    new File([compressedKmzBlob], file.name, { type: file.type }),
  );

  return dataTransfer.files;
}
